import React, { useState, useEffect, useMemo } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { Link } from "react-router-dom";
import DataTable, { createTheme } from "react-data-table-component";
import { useAtom } from "jotai";
import classes from "../../../assets/styles/Contact.module.css";
import { USER, DEVICETYPEUSERCOUNT } from "../../../utils/constants/apiUrl";
import { get, deleteRecord } from "../../../server";
import showToast from "../../../helpers/showToast";
import { loggedInUser } from "../../../utils/store";
import Loader from "../../../components/Loader";
import ConfirmationModal from "../../../components/Modals/ConfirmationModal";
import FormInput from "../../../components/Forms/FormInput";
import searchIcon from "../../../assets/images/global_icons/searchicon.svg";
import formatPhoneNumber from "../../../helpers/formatPhoneNumber";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { Button } from "react-bootstrap";

const AllUsers = () => {
  const [loggedUser, setLoggedUser] = useAtom(loggedInUser);
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [users, setUsers] = useState([]);
  const [count, setCount] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [activeTab, setActiveTab] = useState("");

  // Pagination states
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationLinks, setPaginationLinks] = useState([]);

  const handleFilter = (e) => {
    setFilterText(e.target.value);
  };

  const loadUsers = async (page = 1, size = 10, search = "", device_type = "") => {
    setLoading(true);
    setIsDataLoaded(false);
    setTotalRows(0);

    const params = new URLSearchParams({
      paginate: "true",
      page: page.toString(),
      per_page: size.toString(),
    });

    if (device_type) {
      params.append("device_type", device_type);
    } else if (activeTab) {
      params.append("device_type", activeTab);
    }

    if (search && search.trim() !== "") {
      params.append("search", search.trim());
    }

    let { status, message, data } = await get(
      `${USER}?${params.toString()}`,
      Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
    );

    if (status) {
      setUsers(data.data || []);
      setTotalRows(data.meta?.total || 0);
      setPaginationLinks(data.meta?.links || []);
    } else {
      showToast(status, message);
    }
    setLoading(false);
    setIsDataLoaded(true);
  };

  const handleUsersCounts = async () => {
    setLoading(true);
    let { status, message, data } = await get(
      DEVICETYPEUSERCOUNT,
      Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
    );
    if (status) {
      setCount(data);
    } else {
      showToast(status, message);
    }
    setLoading(false);
  };

  const handleTabChange = (selectedTab) => {
    setActiveTab(selectedTab);
    setCurrentPage(1);
    setTotalRows(0);
    setPerPage(10);
    loadUsers(1, 10, filterText, selectedTab);
  };

  useEffect(() => {
    handleUsersCounts();
  }, []);

  useEffect(() => {
    loadUsers(currentPage, perPage, filterText);
  }, [currentPage, perPage]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadUsers(1, perPage, filterText);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [filterText]);

  createTheme(
    "solarized",
    {
      background: {
        default: "background: var(--secondary_color)",
      },
    },
    "dark"
  );

  const columns = [
    {
      name: "Name",
      width: "200px",
      cell: (row) => <p className="globalDetail">{row.name}</p>,
    },
    {
      name: "Email",
      width: "200px",
      cell: (row) => (
        <a
          href={`mailto:${row.email}`}
          className="globalDetail"
          style={{ display: "block" }}
        >
          {row.email}
        </a>
      ),
    },
    {
      name: "Team Member",
      width: "150px",
      cell: (row) => (
        <p className="globalDetail">
          {row?.connected_teams?.length !== 0 ? "Yes" : "No"}
        </p>
      ),
    },
    {
      name: "Team Name",
      width: "200px",
      cell: (row) => (
        <p className="globalDetail">
          {row?.connected_teams?.length !== 0
            ? row.connected_teams[0].name
            : "-"}
        </p>
      ),
    },
    {
      name: "Phone #",
      width: "150px",
      cell: (row) => (
        <>
          {row.phone_number ? (
            <a
              href={`tel:${
                row.country_phone
                  ? "+" +
                    row.country_phone +
                    " " +
                    formatPhoneNumber(row.phone_number)
                  : row.phone_number || ""
              }`}
              className="globalDetail"
            >
              {row.country_phone
                ? "+" +
                  row.country_phone +
                  " " +
                  formatPhoneNumber(row.phone_number)
                : row.phone_number || ""}
            </a>
          ) : (
            <p className="globalDetail">-</p>
          )}
        </>
      ),
    },
    {
      width: "200px",
      name: "City, Country",
      cell: (row) => (
        <>
          {row.city || row.country ? (
            <a
              href={`http://maps.google.com/?q=${row.city || ""}${
                row.city && row.country ? ", " : ""
              }${row.country || " "}`}
              className="globalDetail"
              target="_blank"
              rel="noreferrer"
            >
              {row.city || ""}
              {row.city && row.country ? "," : ""} {row.country}
            </a>
          ) : (
            <p className="globalDetail">-</p>
          )}
        </>
      ),
    },
    {
      name: "Action",
      width: "100px",
      right: "true",
      cell: (row) => (
        <div className={classes.action}>
          <button
            className="btn customActionDropdown dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <BsThreeDotsVertical className={"globalVerticalThreeDots"} />
          </button>
          <ul className="customActionDropdownMenu dropdown-menu dropdown-menu-end ">
            <li>
              <Link className="dropdown-item" to={`AddEditUser/${row.id}`}>
                Edit
              </Link>
            </li>
            <li>
              <Link
                className="dropdown-item"
                to="#"
                style={{ color: "#E35252" }}
                onClick={() => handleDelete(row.id)}
              >
                Delete
              </Link>
            </li>
          </ul>
        </div>
      ),
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  // Handle confirmation modal
  const handleShowConfirmationModal = () => setShowConfirmationModal(true);
  const handleCloseConfirmationModal = () => setShowConfirmationModal(false);

  // handle delete
  const handleDelete = (id) => {
    setDeleteId(id);
    handleShowConfirmationModal(true);
  };

  const handleConfirmDelete = async () => {
    handleCloseConfirmationModal(true);
    setLoading(true);
    let { status, message } = await deleteRecord(
      `${USER}/${deleteId}`,
      Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
    );
    showToast(status, message);

    if (status) {
      await loadUsers(currentPage, perPage, filterText);
      setLoading(false);
    }
    setLoading(false);
  };

  const totalPages = Math.ceil(totalRows / perPage);

  const renderTableContent = () => (
    <div className="default-list-table">
      <DataTable columns={columns} data={users} theme="solarized" />
      <div className="d-flex justify-content-center align-items-center p-3 flex-wrap gap-4 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="globalDetail">Rows per page:</span>
          <select
            className="form-select"
            style={{ width: "auto", padding: "6px 35px 6px 12px" }}
            value={perPage}
            onChange={(e) => handlePerRowsChange(Number(e.target.value), 1)}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <nav aria-label="Page navigation">
          <ul className="pagination mb-0">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                style={{
                  padding: "8px 16px",
                  minWidth: "45px",
                  textAlign: "center",
                  color: "#58a388",
                }}
              >
                &laquo; Previous
              </button>
            </li>

            {(() => {
              const pages = [];

              if (totalPages <= 0) {
                // No data yet — render nothing
              } else if (totalPages <= 8) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                if (currentPage <= 4) {
                  for (let i = 1; i <= 6; i++) pages.push(i);
                  pages.push("ellipsis-end");
                  pages.push(totalPages);
                } else if (currentPage >= totalPages - 3) {
                  pages.push(1);
                  pages.push("ellipsis-start");
                  for (let i = totalPages - 5; i <= totalPages; i++)
                    pages.push(i);
                } else {
                  pages.push(1);
                  pages.push("ellipsis-start");
                  pages.push(currentPage - 1);
                  pages.push(currentPage);
                  pages.push(currentPage + 1);
                  pages.push("ellipsis-end");
                  pages.push(totalPages);
                }
              }

              return pages.map((page, index) => {
                if (typeof page === "string") {
                  return (
                    <li
                      key={`ellipsis-${index}`}
                      className="page-item disabled"
                    >
                      <span
                        className="page-link"
                        style={{
                          padding: "8px 16px",
                          minWidth: "45px",
                          textAlign: "center",
                          color: "#58a388",
                        }}
                      >
                        ...
                      </span>
                    </li>
                  );
                }
                return (
                  <li
                    key={page}
                    className={`page-item ${
                      currentPage === page ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(page)}
                      style={{
                        backgroundColor:
                          currentPage === page ? "#58a388" : "",
                        borderColor: currentPage === page ? "#58a388" : "",
                        color: currentPage === page ? "#fff" : "#58a388",
                        padding: "8px 16px",
                        minWidth: "45px",
                        textAlign: "center",
                      }}
                    >
                      {page}
                    </button>
                  </li>
                );
              });
            })()}

            <li
              className={`page-item ${
                currentPage === totalPages || totalRows === 0 ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                style={{
                  padding: "8px 16px",
                  minWidth: "45px",
                  textAlign: "center",
                  color: "#58a388",
                }}
              >
                Next &raquo;
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="">
          <div className="page_Header">
            <div className="d-flex justify-content-between align-items-md-center align-items-initial flex-md-row flex-column gap-3 mb-3">
              <div className="heading">
                <h1 className="pageTitle">
                  Users <span>({totalRows})</span>
                </h1>
              </div>
              <div className="d-flex align-items-center justify-content-end gap-2 mb-2">
                <div
                  className="input-group"
                  style={{ maxWidth: "250px", height: "45px" }}
                >
                  <span className="input-group-text bg-white border-end-0">
                    <img
                      src={searchIcon}
                      alt="search"
                      style={{ height: "16px" }}
                    />
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search..."
                    value={filterText}
                    onChange={handleFilter}
                  />
                </div>
                <Link to="/users/addEditUser">
                  <Button variant="primary">Add</Button>
                </Link>
              </div>
            </div>
          </div>

          <Tabs
            defaultActiveKey=""
            activeKey={activeTab}
            transition={false}
            onSelect={handleTabChange}
          >
            <Tab
              eventKey=""
              title={
                <span>
                  All Users <span className="count">({count.allUsers})</span>
                </span>
              }
            >
              {isDataLoaded ? renderTableContent() : <Loader />}
            </Tab>
            <Tab
              eventKey="ios"
              title={
                <span>
                  ios Users <span className="count">({count.ios})</span>
                </span>
              }
            >
              {isDataLoaded ? renderTableContent() : <Loader />}
            </Tab>
            <Tab
              eventKey="android"
              title={
                <span>
                  Android Users <span className="count">({count.andorid})</span>
                </span>
              }
            >
              {isDataLoaded ? renderTableContent() : <Loader />}
            </Tab>
            <Tab
              eventKey="webapp"
              title={
                <span>
                  Web App Users <span className="count">({count.webapp})</span>
                </span>
              }
            >
              {isDataLoaded ? renderTableContent() : <Loader />}
            </Tab>
            <Tab
              eventKey="shopify"
              title={
                <span>
                  Shopify Users <span className="count">({count.shopify})</span>
                </span>
              }
            >
              {isDataLoaded ? renderTableContent() : <Loader />}
            </Tab>
          </Tabs>
          {/* </div> */}

          <ConfirmationModal
            show={showConfirmationModal}
            handleCloseModal={handleCloseConfirmationModal}
            handleConfirmDelete={handleConfirmDelete}
            confirmTitle="user"
          />
        </div>
      )}
    </>
  );
};

export default AllUsers;
